import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../store/store";
import {
  getAllNotifications,
  markGlobalNotificationAsRead,
  markAllGlobalNotificationsAsRead,
  selectAllNotifications,
  selectNotificationsLoading,
  createNotification,
} from "../../../store/slices/notificationSlice";
import Layout from "../../../components/layout/layout";
import { getAuth } from "firebase/auth";
import { useAppState } from "../../../hooks/useAppState";
import { Wallet, Bell, Video, Users, BarChart, Settings } from "lucide-react";
import { AdminNotificationCard } from "./AdminNotificationCard";

/**
 * AdminNotification component specifically for supporty_admin users
 * This component provides a dedicated admin interface for notification management
 */
export function AdminNotification() {
  const dispatch = useDispatch<AppDispatch>();
  const auth = getAuth();
  const { user } = useAppState();
  const userId = auth.currentUser?.uid;
  const allNotifications = useSelector(selectAllNotifications);
  const isLoading = useSelector(selectNotificationsLoading);

  // State for notification filtering
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [filteredNotifications, setFilteredNotifications] = useState<any[]>([]);

  // Admin-specific state
  const [adminStats, setAdminStats] = useState({
    totalNotifications: 0,
    unreadCount: 0,
    fundRequestsCount: 0,
    supportRequestsCount: 0,
    systemNotificationsCount: 0,
  });

  // Filter notifications based on the selected type

  
  useEffect(() => {
    if (activeFilter === "all") {
      setFilteredNotifications(allNotifications);
    } else if (activeFilter === "fund_requests") {
      setFilteredNotifications(
        allNotifications.filter( 
          (notification) =>
            notification.trigger === "fund_request" ||
            notification.trigger === "fund_request_receive" ||
            notification.trigger === "fund_request_send" ||
            notification.trigger === "fund_request_approved" ||
            notification.trigger === "fund_request_rejected" ||
            notification.trigger === "funds_received" ||
            notification.trigger === "funds_added" ||
            notification.trigger === "funds_distributed"
        )
      );
    } else if (activeFilter === "unread") {
      setFilteredNotifications(
        allNotifications.filter((notification) => !notification.read)
      );
    } else if (activeFilter === "support_requests") {
      setFilteredNotifications(
        allNotifications.filter(
          (notification) =>
            notification.trigger === "new_support_request" ||
            notification.trigger === "support_request_submitted" ||
            notification.trigger === "support_request_accepted" ||
            notification.trigger === "support_request_rejected" ||
            notification.trigger === "support_request_missed"
        )
      );
    } else if (activeFilter === "system") {
      setFilteredNotifications(
        allNotifications.filter(
          (notification) =>
            notification.trigger === "system_notification" ||
            notification.trigger === "admin_notification"
        )
      );
    } else {
      setFilteredNotifications(
        allNotifications.filter(
          (notification) =>
            notification.trigger !== "fund_request" &&
            notification.trigger !== "fund_request_receive" &&
            notification.trigger !== "fund_request_approved" &&
            notification.trigger !== "fund_request_rejected" &&
            notification.trigger !== "funds_received" &&
            notification.trigger !== "funds_distributed" &&
            notification.trigger !== "funds_added" &&
            notification.trigger !== "new_support_request" &&
            notification.trigger !== "support_request_submitted" &&
            notification.trigger !== "support_request_accepted" &&
            notification.trigger !== "support_request_rejected" &&
            notification.trigger !== "support_request_missed" &&
            notification.trigger !== "account_created" &&
            notification.trigger !== "system_notification" &&
            notification.trigger !== "admin_notification"
        )
      );
    }
  }, [activeFilter, allNotifications]);

  // Fetch all notifications (admin view)
  useEffect(() => {
    const fetchData = async () => {
      if (userId && user?.role === "supporty_admin") {
        // For admin users, fetch all notifications
        await dispatch(getAllNotifications());
      }
    };

    fetchData();
  }, [dispatch, userId, user?.role]);

  // Calculate admin stats
  useEffect(() => {
    if (allNotifications.length > 0) {
      const unread = allNotifications.filter((n) => !n.read).length;

      const fundRequests = allNotifications.filter(
        (n) =>
          n.trigger === "fund_request" ||
          n.trigger === "fund_request_receive" ||
          n.trigger === "fund_request_send" ||
          n.trigger === "fund_request_approved" ||
          n.trigger === "fund_request_rejected" ||
          n.trigger === "funds_received" ||
          n.trigger === "funds_distributed" ||
          n.trigger === "funds_added"
      ).length;

      const supportRequests = allNotifications.filter(
        (n) =>
          n.trigger === "new_support_request" ||
          n.trigger === "support_request_submitted" ||
          n.trigger === "support_request_accepted" ||
          n.trigger === "support_request_rejected" ||
          n.trigger === "support_request_missed"
      ).length;

      const systemNotifications = allNotifications.filter(
        (n) =>
          n.trigger === "system_notification" ||
          n.trigger === "admin_notification"
      ).length;

      setAdminStats({
        totalNotifications: allNotifications.length,
        unreadCount: unread,
        fundRequestsCount: fundRequests,
        supportRequestsCount: supportRequests,
        systemNotificationsCount: systemNotifications,
      });
    }
  }, [allNotifications]);
  const handleMarkAllAsRead = async () => {
    if (userId && user?.role === "supporty_admin") {
      // Use the bulk mark all as read action
      await dispatch(markAllGlobalNotificationsAsRead());
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    if (userId && notificationId && user?.role === "supporty_admin") {
      // For admin users, mark global notifications as read
      await dispatch(markGlobalNotificationAsRead(notificationId));
    }
  };

  // Check if user is admin
  if (user?.role !== "supporty_admin") {
    console.warn("AdminNotification component accessed by non-admin user");
    return <div>Unauthorized access</div>;
  }

  return (
    <Layout>
      <div className="mx-auto  rounded-lg "  style={{
          height: "calc(100vh - 112px)",
          overflowY: "auto",
            overflowX:'hidden'
        }}>
        <div className="space-y-6 shadow-[0px_2px_4px_0px_#00000033]">
          {/* Admin Notification Header */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between p-2 gap-3">
              <div>
                <h2 className="text-[20px] text-[#696969] nunito-semibold">
                  Notifications
                </h2>
              </div>
              <div>
                <button
                  className="border-[#F15D18] border-[1px] w-[200px] h-[48px] rounded-[8px] text-[16px] text-[#F15D18] nunito-bold"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as Read
                </button>
              </div>
            </div>
            {/* Filter Buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveFilter("all")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                  activeFilter === "all"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveFilter("support_requests")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center ${
                  activeFilter === "support_requests"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                <Video className="w-4 h-4 mr-1" />
                Support (
                {
                  allNotifications.filter(
                    (n) =>
                      n.trigger === "support_request_submitted" ||
                      n.trigger === "new_support_request" ||
                      n.trigger === "support_request_accepted" ||
                      n.trigger === "support_request_rejected" ||
                      n.trigger === "support_request_missed"
                  ).length
                }
                )
              </button>
              <button
                onClick={() => setActiveFilter("fund_requests")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center ${
                  activeFilter === "fund_requests"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                <Wallet className="w-4 h-4 mr-1" />
                Funds (
                {
                  allNotifications.filter(
                    (n) =>
                      n.trigger === "fund_request" ||
                      n.trigger === "fund_request_receive" ||
                      n.trigger === "fund_request_send" ||
                      n.trigger === "fund_request_approved" ||
                      n.trigger === "fund_request_rejected" ||
                      n.trigger === "funds_received" ||
                      n.trigger === "funds_distributed" ||
                      n.trigger === "funds_added"
                  ).length
                }
                )
              </button>

              <button
                onClick={() => setActiveFilter("unread")}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                  activeFilter === "unread"
                    ? "bg-[#F15D18] text-white"
                    : "bg-[#FEE4D0] text-[#F15D18]"
                }`}
              >
                Unread
              </button>
            </div>{" "}
            {/* Notification List */}
            <div className="mt-1 space-y-4 px-2">
              {isLoading ? (
                <div className="text-center py-4">Loading notifications...</div>
              ) : filteredNotifications.length > 0 ? (
                filteredNotifications.map((notification) => (
                  <AdminNotificationCard
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                  />
                ))
              ) : (
                <div className="text-center py-4">No notifications found</div>
              )}
            </div>
          </div>
        </div>
      </div>{" "}
    </Layout>
  );
}
